class CartDiscount extends HTMLElement {
  constructor() {
    super();

    this.input = this.querySelector('.cart-drawer__discount-input');
    this.submitButton = this.querySelector('.cart-drawer__discount-submit');
    this.errorWrapper = this.querySelector('.cart-drawer__discount-error');
    this.errorText = this.errorWrapper ? this.errorWrapper.querySelector('.cart-item__error-text') : null;

    if (this.submitButton) {
      this.submitButton.addEventListener('click', () => this.applyInputCode());
    }

    if (this.input) {
      this.input.addEventListener('keydown', (event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          this.applyInputCode();
        }
      });
    }
  }

  setBusy(isBusy) {
    if (this.submitButton) this.submitButton.disabled = isBusy;
    if (this.input) this.input.disabled = isBusy;
  }

  setError(message) {
    if (!this.errorWrapper || !this.errorText) return;
    this.errorText.textContent = message || '';
    this.errorWrapper.hidden = !message;
  }

  // Automatic discounts can apply at cart level or per line item, so both need checking.
  // Shopify's cart.js reports "" (not "automatic") as the type for automatic discounts in
  // practice, despite the Liquid docs implying otherwise, so this checks for "anything that
  // isn't a discount code" rather than matching an exact "automatic" value.
  cartHasAutomaticDiscount(cart) {
    const isNonCodeDiscount = (type) => type !== 'discount_code';

    const atCartLevel = (cart.cart_level_discount_applications || []).some((discount) =>
      isNonCodeDiscount(discount.type)
    );
    if (atCartLevel) return true;

    return (cart.items || []).some((item) =>
      (item.line_level_discount_allocations || []).some(
        (allocation) => allocation.discount_application && isNonCodeDiscount(allocation.discount_application.type)
      )
    );
  }

  isCodeApplicable(cart, code) {
    const target = code.toLowerCase();
    return (cart.discount_codes || []).some(
      (entry) => (entry.code || '').toLowerCase() === target && entry.applicable
    );
  }

  // Returns the onCartUpdate() promise so callers can act once the refreshed markup
  // (and a brand new <cart-discount> instance) is actually in the DOM.
  refreshDrawer() {
    const cartDrawerItems = document.querySelector('cart-drawer-items');
    if (cartDrawerItems && typeof cartDrawerItems.onCartUpdate === 'function') {
      return cartDrawerItems.onCartUpdate();
    }
    return Promise.resolve();
  }

  postDiscount(code) {
    return fetch(window.routes.cart_update_url, {
      ...fetchConfig(),
      body: JSON.stringify({ discount: code }),
    }).then((response) => response.json());
  }

  removeDiscount() {
    if (this.submitButton && this.submitButton.disabled) return;

    this.setError('');
    this.setBusy(true);

    this.postDiscount('')
      .then(() => this.refreshDrawer())
      .catch((error) => {
        console.error('cart-discount: unable to remove discount code', error);
        this.setError(window.cartStrings.discountCodeError);
      })
      .finally(() => {
        this.setBusy(false);
      });
  }

  applyInputCode() {
    const code = this.input ? this.input.value.trim() : '';

    if (!code) {
      this.setError(window.cartStrings.discountCodeMissing);
      if (this.input) this.input.focus();
      return;
    }

    if (this.submitButton && this.submitButton.disabled) return;

    this.setError('');
    this.setBusy(true);

    // Checked up front, before posting the code, so a successful apply that silently
    // dropped an active automatic discount (Shopify's default behaviour for non-combinable
    // discounts: the code wins outright rather than being rejected) can be told apart from
    // a plain success.
    fetch(`${window.routes.cart_url}.js`, { credentials: 'same-origin' })
      .then((response) => response.json())
      .then((cartBefore) => this.cartHasAutomaticDiscount(cartBefore))
      .then((hadAutomaticDiscount) => this.postDiscount(code).then((cart) => ({ cart, hadAutomaticDiscount })))
      .then(({ cart, hadAutomaticDiscount }) => {
        if (!this.isCodeApplicable(cart, code)) {
          this.setError(
            this.cartHasAutomaticDiscount(cart)
              ? window.cartStrings.discountCodeNotCombinable
              : window.cartStrings.discountCodeInvalid
          );
          return;
        }

        const replacedAutomaticDiscount = hadAutomaticDiscount && !this.cartHasAutomaticDiscount(cart);

        this.refreshDrawer().then(() => {
          if (!replacedAutomaticDiscount) return;
          const refreshedCartDiscount = document.querySelector('cart-discount');
          if (refreshedCartDiscount) {
            refreshedCartDiscount.setError(window.cartStrings.discountCodeReplacedAutomatic);
          }
        });
      })
      .catch((error) => {
        console.error('cart-discount: unable to apply discount code', error);
        this.setError(window.cartStrings.discountCodeError);
      })
      .finally(() => {
        this.setBusy(false);
      });
  }
}

if (!customElements.get('cart-discount')) {
  customElements.define('cart-discount', CartDiscount);
}

// Event delegation: the remove buttons live inside the discount tags rendered per line
// item / cart totals, which get replaced wholesale on every cart re-render, so binding
// once on document avoids having to re-attach listeners after each refresh.
document.addEventListener('click', (event) => {
  const removeButton = event.target.closest('.discounts__remove');
  if (!removeButton) return;
  event.preventDefault();

  const cartDiscount = document.querySelector('cart-discount');
  if (cartDiscount) cartDiscount.removeDiscount();
});
